import { useState } from 'react'
import { useTheme } from 'next-themes'
import { useBrandStore } from '../../stores/brandStore'
import { mediaUrl } from '../../services/config'

// The two shapes the brand mark takes across the app (spec §4.1):
//   <BrandIcon />     — square mark: primary rail, anywhere a 40px logo sits
//   <BrandWordmark /> — logo-or-name lockup: login / register / reset pages
//
// Both degrade in the same order, and the last step is ALWAYS text:
//   brand logo (theme-picked) → stock asset (unbranded deploys only) → text
// A branded tenant with no uploaded logo therefore gets its own name, never a
// broken-image icon.

/**
 * Resolve the brand logo for the active theme. Falls back to the other
 * theme's logo when only one was uploaded — a single-logo tenant is far more
 * common than a two-logo one, and a slightly-off logo beats no logo.
 */
export function useBrandLogoUrl(): string | null {
  const { theme, resolvedTheme } = useTheme()
  const logoLightUrl = useBrandStore((s) => s.brand.logoLightUrl)
  const logoDarkUrl = useBrandStore((s) => s.brand.logoDarkUrl)
  const isDark = (resolvedTheme ?? theme) === 'dark'
  const url = isDark ? (logoDarkUrl ?? logoLightUrl) : (logoLightUrl ?? logoDarkUrl)
  return url ? mediaUrl(url) : null
}

/** "Rx2Go" → "RX", "Acme Health OS" → "AH". Max two glyphs, upper case. */
export function brandInitials(shortName: string): string {
  const words = shortName.trim().split(/[\s_-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

interface BrandIconProps {
  /** Applied to the <img> / initials badge. Callers own the box size. */
  className?: string
}

/**
 * Square brand mark. Renders the tenant logo when there is one; on an
 * unbranded deployment it keeps the compiled-in `logo-icon.svg` (which is what
 * ships today); with neither, an initials badge on the accent surface.
 */
export function BrandIcon({ className = 'w-full h-full object-contain p-1' }: BrandIconProps) {
  const [broken, setBroken] = useState(false)
  const shortName = useBrandStore((s) => s.brand.shortName)
  const isDefault = useBrandStore((s) => s.brand.isDefault)
  const logo = useBrandLogoUrl()

  const src = logo ?? (isDefault ? `${import.meta.env.BASE_URL}logo-icon.svg` : null)

  if (!src || broken) {
    return (
      <span
        className="logo-fallback text-primary-foreground font-bold text-[11px] bg-primary w-full h-full rounded-lg flex items-center justify-center"
        aria-label={shortName}
      >
        {brandInitials(shortName)}
      </span>
    )
  }

  return (
    <img
      src={src}
      alt={shortName}
      className={className}
      onError={() => setBroken(true)}
    />
  )
}

interface BrandWordmarkProps {
  className?: string
}

/**
 * Login-family lockup. A logo IS the wordmark when the tenant uploaded one
 * (its alt carries the name for screen readers); otherwise the name is set as
 * text. Never an <img> without a src — that is the broken-image failure the
 * spec calls out.
 */
export function BrandWordmark({ className = '' }: BrandWordmarkProps) {
  const [broken, setBroken] = useState(false)
  const name = useBrandStore((s) => s.brand.name)
  const logo = useBrandLogoUrl()

  return (
    <div className={`flex items-center justify-center ${className}`}>
      {logo && !broken ? (
        <img
          src={logo}
          alt={name}
          className="h-10 max-w-[220px] object-contain"
          onError={() => setBroken(true)}
        />
      ) : (
        <span className="text-lg font-bold tracking-tight text-foreground">{name}</span>
      )}
    </div>
  )
}
